import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Menu, Transition } from '@headlessui/react';
import React, { Fragment } from 'react';
import { Z_INDEX } from '../../constants/zIndex';
import { useLayerZIndex } from '../../providers/LayerProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';
import { Icon } from '../base/Icon';
import { Text } from '../base/Text';

interface DropdownItem {
	label: string;
	onClick: () => void;
	icon?: React.ReactNode | IconDefinition;
	disabled?: boolean;
	danger?: boolean;
	title?: string;
	/** A rule above this item, to group the menu (PD-17424). */
	dividerBefore?: boolean;
	/** A small heading above this item, naming the group it starts (PD-17455: "Download"). */
	sectionLabel?: string;
	/**
	 * A second, smaller line under the label (PD-14951). Meant for the reason a
	 * disabled item cannot be used yet ("Available once the call is answered"),
	 * which a touch user would otherwise never see because a tooltip needs a
	 * hover. It stays at full strength when the item is disabled, so the reason
	 * is readable while the label is dimmed.
	 */
	description?: string;
}

const isIconDefinition = (value: unknown): value is IconDefinition =>
	typeof value === 'object' &&
	value != null &&
	('iconName' in value || 'prefix' in value);

interface DropdownProps {
	trigger: React.ReactNode;
	items: DropdownItem[];
	align?: 'left' | 'right';
	className?: string;
	/**
	 * Render the menu in a portal anchored to the trigger (Headless UI `anchor`)
	 * so it floats above the page rather than being clipped by an ancestor with
	 * `overflow: auto/hidden`. Use inside scrollable cards/sidebars. Defaults off
	 * (inline absolute positioning) to preserve existing call sites.
	 */
	floating?: boolean;
	/**
	 * Which side of the trigger the menu opens on. `top` is for a trigger that
	 * sits at the bottom of the screen (the in-call bar's More button, PD-14951),
	 * where a menu opening downward would run off the viewport. A `top` menu is
	 * always anchored the way `floating` is, so it can flip back down when there
	 * is no room above. Defaults to `bottom`, which leaves every existing call
	 * site as it was.
	 */
	placement?: 'bottom' | 'top';
}

export function Dropdown({
	trigger,
	items,
	align = 'left',
	className,
	floating = false,
	placement = 'bottom',
}: DropdownProps) {
	const colors = useThemeColors();
	// A floating menu portals to <body>, where it competes with every other
	// overlay; lift it above the one it was opened from (a Popover, a floating
	// window) rather than letting it open underneath (PD-16154). An inline
	// menu stays in its parent's stacking context, where 100 is enough.
	const floatingZIndex = useLayerZIndex(Z_INDEX.dropdown);
	const anchored = floating || placement === 'top';
	const anchorSide = placement === 'top' ? 'top' : 'bottom';
	return (
		<Menu as="div" className={cn('relative inline-block text-left', className)}>
			<Menu.Button as={Fragment}>{trigger}</Menu.Button>

			<Transition
				as={Fragment}
				enter="transition ease-out duration-100"
				enterFrom="transform opacity-0 scale-95"
				enterTo="transform opacity-100 scale-100"
				leave="transition ease-in duration-75"
				leaveFrom="transform opacity-100 scale-100"
				leaveTo="transform opacity-0 scale-95"
			>
				<Menu.Items
					anchor={
						anchored
							? {
									to: `${anchorSide} ${align === 'right' ? 'end' : 'start'}`,
									gap: 8,
								}
							: undefined
					}
					className={cn(
						'min-w-56 rounded-md shadow-lg ring-1 ring-black ring-opacity-5',
						colors.classes.focusRingNone,
						colors.classes.bgPrimary,
						// When anchored, the anchor handles positioning; otherwise fall
						// back to inline absolute positioning relative to the trigger.
						anchored
							? ''
							: cn(
									'absolute z-[100] mt-2 origin-top-right',
									align === 'right' ? 'right-0' : 'left-0',
								),
					)}
					style={anchored ? { zIndex: floatingZIndex } : undefined}
				>
					<div className="py-1">
						{items.map((item, index) => (
							<Fragment key={index}>
							{item.dividerBefore === true && index > 0 && (
								<div
									role="separator"
									className={cn('my-1 border-t', colors.classes.borderDefault)}
								/>
							)}
							{item.sectionLabel != null && (
								<div className={cn('px-3 pb-1 pt-1.5 text-[0.625rem] font-semibold uppercase tracking-wide', colors.classes.textTertiary)}>
									{item.sectionLabel}
								</div>
							)}
							<Menu.Item disabled={item.disabled}>
								{({ active }) => (
									<button
										onClick={item.onClick}
										title={item.title}
										className={cn(
											// The control voice (12px) — menu items sat a size above every
											// button that opens them (owner call 2026-08-03).
											'group flex w-full items-center whitespace-nowrap px-3 py-1.5 text-xs transition-colors',
											item.disabled
												? cn(
														'cursor-not-allowed',
														// With a description, only the label dims (below), so
														// the reason stays readable.
														item.description == null && 'opacity-50',
													)
												: active
													? 'bg-subtle text-default'
													: '',
											!active &&
												(item.danger
													? colors.classes.textDanger
													: colors.classes.textPrimary),
										)}
										disabled={item.disabled}
									>
										{item.description == null ? (
											<>
												{item.icon && (
													<Text
														className={cn(
															'mr-2.5 text-xs',
															colors.classes.textSecondary,
															'group-hover:opacity-80',
														)}
													>
														{isIconDefinition(item.icon) ? (
															<Icon icon={item.icon} />
														) : (
															item.icon
														)}
													</Text>
												)}
												{item.label}
											</>
										) : (
											<span className="flex min-w-0 flex-col items-start text-left">
												<span
													className={cn(
														'flex items-center',
														item.disabled === true && 'opacity-50',
													)}
												>
													{item.icon && (
														<Text
															className={cn(
																'mr-2.5 text-xs',
																colors.classes.textSecondary,
																'group-hover:opacity-80',
															)}
														>
															{isIconDefinition(item.icon) ? (
																<Icon icon={item.icon} />
															) : (
																item.icon
															)}
														</Text>
													)}
													{item.label}
												</span>
												<span
													className={cn(
														'mt-0.5 max-w-[16rem] whitespace-normal text-[0.625rem] leading-snug',
														colors.classes.textSecondary,
													)}
												>
													{item.description}
												</span>
											</span>
										)}
									</button>
								)}
							</Menu.Item>
							</Fragment>
						))}
					</div>
				</Menu.Items>
			</Transition>
		</Menu>
	);
}
